import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import styles from './ChallengeMap.scss'
import { MapV2 } from 'common/MapV2'
import { TrackLine } from 'common/MapV2/TrackLine'
import { Marker } from 'common/Map/Marker'
import { Popup } from 'common/Map/Popup'
import { DivOverlay } from 'common/Map/DivOverlay'
import { Loadable } from 'common/Loadable'
import { Distance } from 'common/units'
import { Card } from 'common/styleguide/Card'
import { initialViewportForTRoute } from 'lib/geoUtils'
import { urls } from 'lib/urls'
import { clubActions, clubSelectors } from 'data/resources/clubs'
import { milestoneActions, milestoneSelectors } from 'data/resources/milestones'
import { routeActions, routeSelectors } from 'data/resources/routes'
import { userActions, userSelectors } from 'data/resources/users'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { withRouter } from 'react-router-dom'
import queryString from 'query-string'

const splitTrackPointsOnDistance = (trackPoints, distance) => {
  if (!distance) { return [null, trackPoints] }
  if (trackPoints[trackPoints.length - 1].distance < distance) { return [trackPoints, null] }
  const dupe = [...trackPoints]
  const lhs = dupe.splice(0, dupe.findIndex(tp => tp.distance > distance))
  return [[...lhs, dupe[0]], dupe] // so lines connect
}

const findPositionsForMilestones = (trackPoints, milestones, routeDistance) => (
  milestones?.map(m => {
    const distance = m.targetType === 'percent'
      ? routeDistance * (m.targetValue / 100)
      : m.targetValue
    return {
      milestone: m,
      position: trackPoints[trackPoints.findIndex(tp => tp.distance > distance)]
    }
  })
)

export const _ChallengeMap = ({ user, userFromParams, route, club, amountCompleted, milestones, participationType, location }) => {
  const initialViewport = initialViewportForTRoute(route)
  const [trackPoints, setTrackPoints] = useState([null, null])
  const [milestonesAndPositions, setMilestonesAndPositions] = useState(null)
  const [selected, setSelected] = useState(null)
  const amountCompletedFromParams = parseFloat(queryString.parse(location?.search)?.amountCompleted)

  useMemo(() => {
    setTrackPoints(splitTrackPointsOnDistance(route.trackPoints, (amountCompleted || amountCompletedFromParams)))
  }, [])
  useMemo(() => {
    setMilestonesAndPositions(findPositionsForMilestones(route.trackPoints, milestones, route.distance))
  }, [])

  const [completedTrackPoints, remainingTrackPoints] = trackPoints
  if (!(completedTrackPoints || remainingTrackPoints)) {
    return <></>
  }
  let markerSrc
  if (participationType === 'community') {
    markerSrc = club?.logo
  } else {
    markerSrc = (user || userFromParams).highlightedPhotoId > 0
      ? urls.photos.milli((user || userFromParams).highlightedPhotoId)
      : urls.users.profileDefault.small((user || userFromParams).id)
  }

  return (
    <div className={styles.challengeMap}>
      <MapV2
        hideOverlayPicker
        initialViewport={initialViewport}
        scrollWheelZoom={false}
      >
        {(user || userFromParams) && completedTrackPoints && <>
          <TrackLine
            color='#0B24FB'
            showStartMarker={false}
            showEndMarker={false}
            trackPoints={completedTrackPoints}
          />
          <UserMarker position={completedTrackPoints[completedTrackPoints.length - 1]} src={markerSrc} />
      </>}
        {milestonesAndPositions?.map(({ milestone, position }) => (
          <MilestoneMarker
            key={milestone.id}
            position={position}
            milestone={milestone}
            selected={selected}
            onClick={() => setSelected({ milestone, position })}
          />
        )).toIndexedSeq()}
        {selected && <Popup position={selected.position} closeButton onRequestClose={() => setSelected(null)}>
          <div className={styles.mPopup}>
            <img src={selected.milestone.badge} />
            <div className={styles.right}>
              <div className={styles.title}>{selected.milestone.title}</div>
              <div className={styles.subInfo}>{selected.milestone.overview}</div>
              <div className={styles.subInfo}>
                {selected.milestone.targetType === 'percent'
                  ? `${selected.milestone.targetValue}%`
                  : <Distance bigUnits short value={selected.milestone.targetValue} />}
              </div>
            </div>
          </div>
        </Popup>}
        {remainingTrackPoints && <TrackLine
          showStartMarker={false}
          showEndMarker={false}
          trackPoints={remainingTrackPoints}
        />}
      </MapV2>
    </div>
  )
}

// we look in props and then params.location for privacyCode, clubId, and routeId
// because we could be rendering in a browser (in which case we'll get information
// from props) or inside a webview (in which case we'll get information from query params)
const LoadableChallengeMap = ({ loadRoute, loadClub, loadUser, loadMilestones, loadOwnMilestones, ...props }) => {
  const parsed = queryString.parse(props.location.search)
  const privacyCode = props.route?.privacyCode || parsed?.privacyCode
  const clubId = props.clubId || parsed?.clubId
  const userId = parsed?.userId
  // we need to block render on the route load because the route we likely got
  // back in the show response is sparse
  const resourceLoaded = () => Promise.all([
    loadRoute(privacyCode),
    clubId && loadClub(),
    userId && loadUser(userId),
    loadOwnMilestones && loadMilestones()
  ].filter(p => p))

  const participationTypeProp = { participationType: props.participationType || props.location?.query?.participationType }
  return <Loadable
    component={_ChallengeMap}
    resourceLoaded={resourceLoaded}
    {...participationTypeProp}
    {...props}
  />
}

const mapStateToProps = (state, ownProps) => {
  const parsed = queryString.parse(ownProps.location.search)
  const routeId = ownProps.routeId || parseInt(parsed?.routeId, 10)
  const clubId = ownProps.clubId || parseInt(parsed?.clubId, 10)
  const userId = parseInt(parsed?.userId, 10)
  const goalId = parseInt(ownProps.match?.params?.challengeId, 10)
  return Object.assign({
    route: routeSelectors.find(state, routeId),
    club: clubSelectors.find(state, clubId),
    userFromParams: userSelectors.find(state, userId)
  },
  !ownProps.milestones && { milestones: milestoneSelectors.byGoalId(state, goalId), loadOwnMilestones: true })
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const parsed = queryString.parse(ownProps.location.search)
  const routeId = ownProps.routeId || parsed?.routeId
  const clubId = ownProps.clubId || parsed?.clubId
  const userId = parsed?.userId
  const goalPrivacyCode = parsed?.goalPrivacyCode
  const goalId = parseInt(ownProps.match?.params?.challengeId, 10)
  return {
    loadRoute: privacyCode => dispatch(routeActions.read(routeId, privacyCode && { privacyCode })),
    loadClub: () => dispatch(clubActions.read(clubId)),
    loadUser: () => dispatch(userActions.read(userId)),
    loadMilestones: () => dispatch(milestoneActions.list({ goalId, privacyCode: goalPrivacyCode }))
  }
}

export const ChallengeMap = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableChallengeMap))

export const ChallengeMapCard = props => <Card noPadding className={styles.challengeMapCard}>
  <ChallengeMap {...props} />
</Card>

ChallengeMap.propTypes = {
  routeId: PropTypes.number,
  clubId: PropTypes.number,
  participationType: PropTypes.string,
  user: PropTypes.object,
  amountCompleted: PropTypes.number,
  milestones: PropTypes.instanceOf(Map)
}

const MilestoneMarker = ({ position, milestone, selected, onClick }) => {
  if (milestone.id === selected?.milestone?.id) { return null }
  return <Marker icon='milestone' position={position} onClick={onClick} />
}

const UserMarker = ({ position, src }) => {
  return <DivOverlay
    position={position}
  >
    <div className={styles.userMarker}>
      <div className={styles.img}>
        <img src={src} />
      </div>
      <div className={styles.bar} />
      <div className={styles.ellipse} />
    </div>
  </DivOverlay>
}
